import { FC, forwardRef } from "react";
import { useController, UseControllerProps, useFormContext } from "react-hook-form";
import {
  StyleSheet,
  Switch,
  SwitchProps,
  Text,
  TextInput,
  TextInputProps,
  TextProps,
  TouchableOpacity,
  View,
} from "react-native";

import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { formatPhoneNumber } from "src/util/gFormatting";
import { PdCheckbox, PdCheckboxGroup, tPdCheckbox, tPdCheckboxGroup } from "../forms/checkbox";

import {
  PdDateRangeInput,
  PdInput,
  PdInputFloat,
  PdInputNumber,
  PdInputPdf,
  PdInputPercentage,
  tPdInputFloatProps,
  tPdInputNumberProps,
  tPdInputPdf,
  tPdInputPercentageProps,
  tPdInputProps,
  tPdRangeDateInputProps,
} from "../forms/input";
import { PdImagePicker, tPdImagePicker } from "../forms/picker";
import { PdRadioGroup, tPdRadioGroup } from "../forms/radio";
import { PdSelect, PdSelectMultiple, tPdSelect, tPdSelectMultiple } from "../forms/select";
import { PdSwitch } from "../forms/switch";
import { mergeRefs } from "../utils";

export const PdHookFormInput = forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & tPdInputProps & { isSpacesAllowed?: boolean }
>((props, ref) => {
  const { name, rules, value, onChangeText, isSpacesAllowed = true, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChangeText = (text: string) => {
    const _text = isSpacesAllowed ? text : text.replace(/[^a-zA-Z0-9]/g, "");
    field.onChange(_text);
    onChangeText?.(_text);
  };

  return (
    <PdInput
      value={field.value}
      onChangeText={handleOnChangeText}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormInputPhone = forwardRef<TextInput, Pick<UseControllerProps, "name" | "rules"> & TextInputProps>(
  (props, ref) => {
    const { name, rules, value, ...rest } = props;
    const { control } = useFormContext();
    const { field } = useController({ name, rules, control });
    return (
      <PdInput
        value={formatPhoneNumber(field.value)}
        onChangeText={(newValue) => field.onChange(formatPhoneNumber(newValue))}
        onBlur={field.onBlur}
        ref={mergeRefs([ref, field.ref])}
        {...rest}
      />
    );
  },
);

export const PdHookFormInputNumber = forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & Omit<tPdInputNumberProps, "value" | "onChangeText">
>((props, ref) => {
  const { name, rules, onChangeNumber, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChange: tPdInputNumberProps["onChangeNumber"] = (value) => {
    field.onChange(value);
    onChangeNumber?.(value);
  };

  return (
    <PdInputNumber
      value={field.value}
      onChangeNumber={handleOnChange}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormInputFloat = forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & Omit<tPdInputFloatProps, "value">
>((props, ref) => {
  const { name, rules, onChangeText, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChange: tPdInputFloatProps["onChangeText"] = (value) => {
    field.onChange(value);
    onChangeText?.(value);
  };

  return (
    <PdInputFloat
      value={field.value}
      onChangeText={handleOnChange}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormInputPercentage = forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & tPdInputPercentageProps
>((props, ref) => {
  const { value: _, name, rules, onChangeNumber, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChangeNumber = (value: number | null) => {
    field.onChange(value);
    onChangeNumber?.(value);
  };

  return (
    <PdInputPercentage
      value={field.value}
      onChangeNumber={handleOnChangeNumber}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormDateRangeInput = forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> &
    Omit<tPdRangeDateInputProps, "period" | "onChange"> & { onChange?: tPdRangeDateInputProps["onChange"] }
>((props, ref) => {
  const { name, rules, onChange, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChange: tPdRangeDateInputProps["onChange"] = (newPeriod, event) => {
    field.onChange(newPeriod);
    onChange?.(newPeriod, event);
  };

  return (
    <PdDateRangeInput period={field.value} onChange={handleOnChange} ref={mergeRefs([ref, field.ref])} {...rest} />
  );
});

export const PdHookFormInputPdf = forwardRef<View, Pick<UseControllerProps, "name" | "rules"> & tPdInputPdf>(
  (props, ref) => {
    const { name, rules, onChange, ...rest } = props;
    const { control } = useFormContext();
    const { field } = useController({ name, rules, control });

    const handleOnChange: tPdInputPdf["onChange"] = (value) => {
      field.onChange(value);
      onChange?.(value);
    };

    return <PdInputPdf value={field.value} onChange={handleOnChange} ref={mergeRefs([ref, field.ref])} {...rest} />;
  },
);

export const PdHookFormSelect = forwardRef<TextInput, Pick<UseControllerProps, "name" | "rules"> & tPdSelect>(
  (props, ref) => {
    const { name, rules, value, onChange, ...rest } = props;
    const { control } = useFormContext();
    const { field } = useController({ name, rules, control });

    const handleOnChange: tPdSelect["onChange"] = (option) => {
      field.onChange(option);
      onChange?.(option);
    };

    return (
      <PdSelect
        value={field.value}
        onChange={handleOnChange}
        onBlur={field.onBlur}
        ref={mergeRefs([ref, field.ref])}
        {...rest}
      />
    );
  },
);

export const PdHookFormSelectMultiple = forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & tPdSelectMultiple
>((props, ref) => {
  const { name, rules, value, onChange, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChange: tPdSelectMultiple["onChange"] = (option) => {
    field.onChange(option);
    onChange?.(option);
  };

  return (
    <PdSelectMultiple
      value={field.value}
      onChange={handleOnChange}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormSwitch = forwardRef<Switch, Pick<UseControllerProps, "name" | "rules"> & SwitchProps>(
  (props, ref) => {
    const { name, rules, onValueChange, ...rest } = props;
    const { control } = useFormContext();
    const { field } = useController({ name, rules, control });

    const handleOnValueChange: SwitchProps["onValueChange"] = (value) => {
      field.onChange(value);
      onValueChange?.(value);
    };

    return (
      <PdSwitch value={field.value} onValueChange={handleOnValueChange} ref={mergeRefs([ref, field.ref])} {...rest} />
    );
  },
);

export const PdHookFormCheckbox = forwardRef<
  TouchableOpacity,
  Pick<UseControllerProps, "name" | "rules"> & React.PropsWithChildren<tPdCheckbox>
>((props, ref) => {
  const { name, rules, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });
  return <PdCheckbox {...rest} ref={mergeRefs([ref, field.ref])} value={field.value} onChange={field.onChange} />;
});

export const PdHookFormCheckboxGroup = forwardRef<
  TouchableOpacity,
  Pick<UseControllerProps, "name" | "rules"> & React.PropsWithChildren<tPdCheckboxGroup>
>((props, ref) => {
  const { name, rules, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });
  return <PdCheckboxGroup {...rest} ref={mergeRefs([ref, field.ref])} value={field.value} onChange={field.onChange} />;
});

export const PdHookFormRadioGroup: React.FC<Pick<UseControllerProps, "name" | "rules"> & tPdRadioGroup> = (props) => {
  const { name, rules, onChange, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnValueChange: tPdRadioGroup["onChange"] = (value) => {
    field.onChange(value);
    onChange?.(value);
  };

  return <PdRadioGroup {...rest} value={field.value} onChange={handleOnValueChange} />;
};

export const PdHookFormImagePicker: React.FC<Pick<UseControllerProps, "name" | "rules"> & tPdImagePicker> = (props) => {
  const { name, rules, onChange, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnValueChange: tPdImagePicker["onChange"] = (value) => {
    field.onChange(value);
    onChange?.(value);
  };

  return <PdImagePicker {...rest} source={field.value} onChange={handleOnValueChange} />;
};

export const PdHookFormErrorMessage: FC<Pick<UseControllerProps, "name"> & TextProps> = ({ name, style, ...rest }) => {
  const { formState } = useFormContext();

  if (!formState.errors[name]) {
    return null;
  }

  return (
    <Text style={[styles.hookFormErrorMessage, style]} {...rest}>
      {formState.errors[name].message}
    </Text>
  );
};

const styles = StyleSheet.create({
  hookFormErrorMessage: {
    ...kFonts.ralewayExtraSmallSemibold,
    color: kColors.red,
    letterSpacing: 1,
    lineHeight: 18,
    textTransform: "uppercase",
  },
});
